/**
 * NOS-46: Events list page with Upcoming/Past/Drafts tabs.
 * Gated by can_manage_events.
 */

import { useState } from "react"
import { Link } from "react-router-dom"
import { Calendar, Loader2, MapPin, Pencil, Plus, Search, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { PermissionGate } from "@/components/PermissionGate"
import { Navigate } from "react-router-dom"
import { useOrgStore } from "@/stores/orgStore"
import { formatEventDate } from "@/lib/format"
import {
  useEvents,
  useDeleteEvent,
  type EventTabFilter,
} from "@/hooks/useEvents"
import { useEventRegistrationHeadcountByEvent } from "@/hooks/useEventRegistrations"
import { toast } from "sonner"

export function EventsPage() {
  const { currentOrg } = useOrgStore()
  const orgId = currentOrg?.id ?? null

  const [tabFilter, setTabFilter] = useState<EventTabFilter>("upcoming")
  const [search, setSearch] = useState("")
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const { events, loading, error, refresh } = useEvents(orgId, {
    tab: tabFilter,
    search: search || undefined,
  })
  const { deleteEvent } = useDeleteEvent(orgId)
  const registrationCounts = useEventRegistrationHeadcountByEvent(
    orgId,
    events.map((e) => e.id)
  )

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    const ok = await deleteEvent(deleteId)
    setDeleting(false)
    setDeleteId(null)
    if (ok) {
      toast.success("Event deleted")
      refresh()
    } else {
      toast.error("Failed to delete event")
    }
  }

  return (
    <PermissionGate permission="can_manage_events" fallback={<Navigate to="/dashboard" replace />}>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Events</h1>
            <p className="text-muted-foreground">
              Create and manage events for your website.
            </p>
          </div>
          <Button asChild>
            <Link to="/dashboard/events/new">
              <Plus className="mr-2 h-4 w-4" />
              New event
            </Link>
          </Button>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <Tabs value={tabFilter} onValueChange={(v) => setTabFilter(v as EventTabFilter)}>
            <TabsList>
              <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
              <TabsTrigger value="past">Past</TabsTrigger>
              <TabsTrigger value="drafts">Drafts</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search events..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-lg border border-dashed py-16 text-center">
            <Calendar className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">
              {tabFilter === "upcoming" && "No upcoming events."}
              {tabFilter === "past" && "No past events."}
              {tabFilter === "drafts" && "No drafts."}
            </p>
            <Button asChild variant="outline" className="mt-4">
              <Link to="/dashboard/events/new">Create your first event</Link>
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => (
              <Card key={event.id} className="overflow-hidden">
                <div className="aspect-video overflow-hidden bg-muted">
                  {event.cover_image ? (
                    <img
                      src={event.cover_image}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <Calendar className="h-12 w-12 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <Link
                      to={`/dashboard/events/${event.id}/edit`}
                      className="font-semibold hover:underline"
                    >
                      {event.title}
                    </Link>
                    {!event.is_published && (
                      <Badge variant="secondary">Draft</Badge>
                    )}
                  </div>
                  <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4 shrink-0" />
                    {formatEventDate(event.start_date, event.end_date)}
                  </p>
                  {event.registration_enabled && (
                    <p className="mt-2">
                      <Link to={`/dashboard/events/${event.id}/registrations`}>
                        <Badge variant="outline" className="font-normal hover:bg-muted">
                          {(registrationCounts[event.id] ?? 0) === 0
                            ? "Registrants"
                            : `${registrationCounts[event.id] ?? 0} registrants`}
                        </Badge>
                      </Link>
                    </p>
                  )}
                  {event.location_name && (
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4 shrink-0" />
                      {event.location_name}
                    </p>
                  )}
                  <div className="mt-4 flex gap-2">
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`/dashboard/events/${event.id}/edit`}>
                        <Pencil className="mr-1.5 h-4 w-4" />
                        Edit
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(event.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="mr-1.5 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete event?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Delete"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </PermissionGate>
  )
}
